<template>
  <v-container fluid class="pa-6">
    <!-- KPI Cards Section -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="primary">mdi-barcode-scan</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Referencias Únicas</div>
              <div class="kpi-value">{{ countUniqueItems("sku") }}</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="info">mdi-package-variant</v-icon>
                {{ inventory_filter.length || 0 }} productos totales
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="success">mdi-tag-multiple</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Marcas</div>
              <div class="kpi-value">{{ countUniqueItems("marca") }}</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="success">mdi-shape</v-icon>
                {{ countUniqueItems("categoria") }} categorías
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="info">mdi-palette-swatch</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Variedad de Color</div>
              <div class="kpi-value">{{ countUniqueItems("color") }}</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="purple">mdi-ruler</v-icon>
                {{ countUniqueSizes() }} tallas
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="warning">mdi-alert-circle-outline</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Stock Mínimo</div>
              <div class="kpi-value">{{ calculateTotalMinStock() }}</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="warning">mdi-trending-up</v-icon>
                Unidades de seguridad
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content Card -->
    <v-card class="main-card elevation-0" outlined>
      <!-- Header with Actions -->
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h2 class="section-title">Maestro de Productos</h2>
          <v-spacer></v-spacer>

          <!-- Search and Filters -->
          <v-autocomplete
            v-model="filters.id_proveedor"
            :items="suppliers"
            label="Proveedor"
            item-value="code"
            item-text="nombre"
            outlined
            hide-details
            return-object
            dense
            clearable
            class="mr-4"
            style="max-width: 250px"
          />

          <v-text-field
            v-model="filters.refe"
            label="Referencia"
            clearable
            outlined
            @keyup="filterData"
            hide-details
            dense
            class="mr-4"
            style="max-width: 200px"
          />

          <v-btn
            color="primary"
            @click="get_inventory"
            :disabled="!filters.id_proveedor"
            class="mr-2"
          >
            <v-icon left>mdi-magnify</v-icon>
            Buscar
          </v-btn>

          <v-btn
            color="primary"
            @click="newForm"
            :disabled="!canCreateProduct()"
            :title="
              !canCreateProduct()
                ? 'Seleccione un proveedor primero'
                : 'Crear nuevo producto'
            "
          >
            <v-icon left>mdi-plus</v-icon>
            Nuevo
          </v-btn>

          <v-btn icon class="ml-2" @click="exportToCSV" title="Exportar a CSV">
            <v-icon>mdi-file-export</v-icon>
          </v-btn>
        </v-toolbar>
      </v-card-title>

      <!-- Data Table -->
      <v-data-table
        :headers="minimalHeaders"
        :items="inventory_filter"
        :items-per-page="10"
        :search="search"
        item-key="ean"
        :loading="loading_status"
        loader-height="3"
        loading-text="Cargando ..."
        :sort-by="['nombre']"
        :sort-desc="[false]"
        class="minimal-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
        }"
      >
        <!-- Product Info -->
        <template v-slot:[`item.product`]="{ item }">
          <div class="product-info">
            <div class="product-name">
              <v-icon small class="mr-2" color="primary">mdi-package-variant</v-icon>
              {{ item.nombre }}
            </div>
            <div class="product-details">
              <span class="mr-3">
                <v-icon x-small class="mr-1">mdi-barcode</v-icon>
                {{ item.ean }}
              </span>
              <span class="mr-3">
                <v-icon x-small class="mr-1">mdi-identifier</v-icon>
                {{ item.sku }}
              </span>
              <span v-if="item.plu">
                <v-icon x-small class="mr-1">mdi-numeric</v-icon>
                {{ item.plu }}
              </span>
            </div>
          </div>
        </template>

        <!-- Category & Brand -->
        <template v-slot:[`item.category`]="{ item }">
          <div class="category-info">
            <div>{{ item.categoria }}</div>
            <div class="text-caption grey--text">
              {{ item.marca }}
            </div>
          </div>
        </template>

        <!-- Reference & Color -->
        <template v-slot:[`item.reference`]="{ item }">
          <div class="reference-info">
            <div>
              <v-icon x-small class="mr-1">mdi-tag</v-icon>
              {{ item.refe }}
            </div>
            <div v-if="item.color" class="text-caption">
              <v-icon x-small class="mr-1">mdi-palette</v-icon>
              {{ item.color }}
            </div>
          </div>
        </template>

        <!-- Size Info -->
        <template v-slot:[`item.size`]="{ item }">
          <div class="size-info">
            <v-chip small label>{{ item.talla }}</v-chip>
            <div class="text-caption grey--text mt-1">{{ item.tallas }}</div>
          </div>
        </template>

        <!-- Pricing -->
        <template v-slot:[`item.pricing`]="{ item }">
          <div class="pricing-info">
            <div class="price-main">{{ formatCurrency(item.precio) }}</div>
            <div class="price-cost text-caption">
              Costo: {{ formatCurrency(item.costo) }}
            </div>
            <div
              v-if="item.descuento > 0"
              class="price-discount text-caption error--text"
            >
              -{{ formatCurrency(item.descuento) }}
            </div>
          </div>
        </template>

        <!-- Stock -->
        <template v-slot:[`item.stock`]="{ item }">
          <div class="stock-info">
            <div class="text-center">
              <v-icon small color="warning" class="mr-1">mdi-alert-circle-outline</v-icon>
              Min: {{ item.cantidad || 0 }}
            </div>
          </div>
        </template>

        <!-- Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn icon small color="primary" @click="get_curva(item)" title="Ver detalles">
            <v-icon small>mdi-magnify</v-icon>
          </v-btn>

          <v-btn
            icon
            small
            color="warning"
            @click="editItem(item)"
            title="Editar producto"
            class="ml-1"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
        </template>

        <!-- No data -->
        <template v-slot:no-data>
          <div class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-package-variant</v-icon>
            <h3 class="text-h6 mt-4 grey--text">No hay productos registrados</h3>
            <v-btn color="primary" class="mt-4" @click="newForm">
              <v-icon left>mdi-plus</v-icon>
              Agregar Primer Producto
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Product Dialog -->
    <ProductForm
      :dialog="Product_dialog"
      @close="handleProductClose"
      @refresh="handleProductRefresh"
      :item="item"
      :newItem="item.ean ? false : true"
      :suppliers="suppliers"
      table="items"
    />

    <!-- SKU Details Dialog -->
    <v-dialog v-model="sku_dialogo" scrollable max-width="1200px">
      <v-card class="elevation-0">
        <v-toolbar flat class="minimal-toolbar">
          <h3 class="dialog-title">Detalle de SKUs</h3>
          <v-spacer></v-spacer>
          <v-btn icon @click="sku_dialogo = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-form
            v-model="price_valid"
            ref="priceChangeform"
            @submit.prevent="price_change"
          >
            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="new_cost"
                  label="Nuevo costo"
                  outlined
                  dense
                  prepend-inner-icon="mdi-currency-usd"
                  clearable
                  @change="update_selected_values"
                  :rules="f_required"
                  hide-details="auto"
                  type="number"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="new_price"
                  label="Nuevo precio"
                  outlined
                  dense
                  prepend-inner-icon="mdi-currency-usd"
                  clearable
                  @change="update_selected_values"
                  :rules="f_required"
                  hide-details="auto"
                  type="number"
                />
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center">
                <v-btn
                  color="warning"
                  type="submit"
                  :disabled="!price_valid || selected_skus.length === 0"
                  block
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Aplicar cambio
                </v-btn>
              </v-col>
            </v-row>
          </v-form>

          <v-data-table
            v-model="selected_skus"
            :headers="header_sku"
            :items="sku_lst"
            :items-per-page="10"
            mobile-breakpoint="0"
            class="elevation-1"
            :loading="loading_status"
            loader-height="3"
            loading-text="Cargando ..."
            show-select
            item-key="ean"
            :sort-by="['talla']"
            :sort-desc="[false]"
          >
            <!-- Format pricing columns -->
            <template v-slot:[`item.costo`]="{ item }">
              <span class="font-weight-medium">{{ formatCurrency(item.costo) }}</span>
            </template>
            <template v-slot:[`item.precio`]="{ item }">
              <span class="font-weight-medium">{{ formatCurrency(item.precio) }}</span>
            </template>
            <template v-slot:[`item.descuento`]="{ item }">
              <span class="font-weight-medium">{{ formatCurrency(item.descuento) }}</span>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-6">
          <v-btn
            color="primary"
            outlined
            :disabled="selected_skus.length === 0"
            @click="openLabelDialog"
          >
            <v-icon left>mdi-label-multiple-outline</v-icon>
            Etiquetas ({{ selected_skus.length }})
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="sku_dialogo = false"> Cancelar </v-btn>
          <v-btn
            color="primary"
            text
            @click="update_sku_values"
            :disabled="sku_lst.length === 0"
          >
            <v-icon left>mdi-content-copy</v-icon>
            Aplicar primer valor a todos
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Dialog -->
    <v-dialog v-model="loading_status" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-card-text class="pa-4">
          <v-row align="center" no-gutters>
            <v-col cols="auto" class="mr-3">
              <v-progress-circular indeterminate color="white"></v-progress-circular>
            </v-col>
            <v-col>
              <div class="subtitle-1">Procesando información</div>
              <div class="text-caption">Por favor espere...</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Label Editor Dialog (WYSIWYG) -->
    <v-dialog v-model="showLabelDialog" max-width="1000px" scrollable>
      <v-card>
        <v-toolbar flat dense color="white">
          <v-icon left color="primary">mdi-label-multiple-outline</v-icon>
          <v-toolbar-title class="subtitle-1 font-weight-medium">Editor de Etiquetas</v-toolbar-title>
          <v-spacer />
          <v-chip small outlined color="primary" class="mr-2">
            {{ totalLabelCount }} etiqueta{{ totalLabelCount !== 1 ? 's' : '' }}
          </v-chip>
          <v-btn icon small @click="showLabelDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-divider />

        <v-card-text class="pa-0" style="max-height: 620px; overflow: auto;">
          <v-row no-gutters>

            <!-- Panel izquierdo: Campos -->
            <v-col cols="3" class="wysiwyg-config-panel pa-3">
              <div class="editor-section-label mb-2">PAPEL</div>
              <v-btn-toggle v-model="labelFormat" mandatory dense class="format-toggle mb-4">
                <v-btn value="cab" x-small class="format-btn">
                  <v-icon x-small left>mdi-view-grid</v-icon>CAB 33×25
                </v-btn>
                <v-btn value="zebra" x-small class="format-btn">
                  <v-icon x-small left>mdi-label-outline</v-icon>Zebra 50×30
                </v-btn>
              </v-btn-toggle>

              <div class="editor-section-label mb-1">CAMPOS</div>
              <div class="caption grey--text mb-2" style="font-size:0.62rem">
                ojo=mostrar · +/- = tamaño fuente
              </div>
              <div
                v-for="field in labelFields"
                :key="field.id"
                class="wysiwyg-field-row"
                :class="{ 'wysiwyg-field-selected': selectedFieldId === field.id }"
                @click="selectedFieldId = field.id"
              >
                <v-btn icon x-small class="mr-1" @click.stop="toggleFieldVisibility(field)">
                  <v-icon x-small :color="field.visible ? 'primary' : 'grey lighten-1'">
                    {{ field.visible ? 'mdi-eye' : 'mdi-eye-off' }}
                  </v-icon>
                </v-btn>

                <span class="text-caption flex-grow-1 label-field-name"
                  :class="field.visible ? 'black--text' : 'grey--text'">
                  {{ field.label }}
                </span>

                <v-btn icon x-small @click.stop="changeFontSize(field, -1)">
                  <v-icon x-small>mdi-minus</v-icon>
                </v-btn>
                <span class="font-size-badge">{{ field.fontSize }}</span>
                <v-btn icon x-small @click.stop="changeFontSize(field, +1)">
                  <v-icon x-small>mdi-plus</v-icon>
                </v-btn>

              </div>

              <v-btn x-small text color="grey" class="mt-3" block @click="resetLabelPositions">
                <v-icon x-small left>mdi-restore</v-icon>Restablecer
              </v-btn>
            </v-col>

            <v-divider vertical />

            <!-- Panel derecho: Canvas + Tabla -->
            <v-col cols="9" class="pa-4">
              <div class="editor-section-label mb-1">
                DISEÑO
                <span class="grey--text text-caption font-weight-regular ml-2">
                  arrastra los campos · {{ labelFormat === 'cab' ? '33×25 mm · CAB · 3 col/hoja' : '50×30 mm · Zebra · individual' }}
                </span>
              </div>
              <div class="canvas-area mb-4">
                <div
                  class="label-canvas"
                  :style="{ width: labelCanvasW + 'px', height: labelCanvasH + 'px' }"
                  @mousemove.prevent="onCanvasMouseMove"
                  @mouseup="onCanvasMouseUp"
                  @mouseleave="onCanvasMouseUp"
                >
                  <template v-for="field in labelFields">
                  <div
                    v-if="field.visible"
                    :key="field.id"
                    class="canvas-field"
                    :class="{
                      'canvas-field-selected': selectedFieldId === field.id,
                      'canvas-field-dragging': draggingFieldId === field.id,
                    }"
                    :style="getFieldCanvasStyle(field)"
                    @mousedown.prevent="startFieldDrag($event, field)"
                    @click.stop="selectedFieldId = field.id"
                  >
                    <template v-if="field.id === 'ean'">
                      <div class="canvas-barcode"
                        :style="{ height: Math.round(labelScale * (labelFormat === 'cab' ? 3.5 : 5)) + 'px', width: Math.round(labelCanvasW * 0.68) + 'px' }">
                      </div>
                      <div :style="{ fontSize: getFieldCanvasFontSize(field) + 'px', fontFamily: 'monospace', marginTop: '1px', lineHeight: 1 }">
                        {{ getPreviewItem().ean || '7706000000000' }}
                      </div>
                    </template>
                    <template v-else>
                      <span :style="{
                        fontSize: getFieldCanvasFontSize(field) + 'px',
                        fontWeight: field.id === 'precio' ? '900' : 'normal',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.1,
                      }">{{ getFieldDisplayValue(field) }}</span>
                    </template>
                  </div>
                  </template>
                </div>
              </div>

              <v-divider class="mb-3" />

              <div class="editor-section-label mb-2">CANTIDAD POR TALLA
                <span class="grey--text text-caption font-weight-regular ml-2">click = previsualizar</span>
              </div>
              <v-simple-table dense class="label-items-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">EAN</th>
                      <th class="text-left">Talla · Color</th>
                      <th class="text-right" style="width:130px">Cantidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, i) in labelItems"
                      :key="item.ean"
                      class="qty-row"
                      :class="{ 'active-preview-row': i === previewIndex }"
                      @click="previewIndex = i"
                    >
                      <td class="text-caption grey--text">{{ item.ean }}</td>
                      <td class="text-caption">
                        <v-chip x-small label color="primary" outlined class="mr-1">
                          {{ item.talla || '—' }}
                        </v-chip>
                        <span v-if="item.color" class="grey--text">{{ item.color }}</span>
                      </td>
                      <td>
                        <div class="d-flex align-center justify-end">
                          <v-btn icon x-small @click.stop="adjustQty(i, -1)" :disabled="item.cantidadImprimir <= 0">
                            <v-icon x-small>mdi-minus</v-icon>
                          </v-btn>
                          <span class="qty-value mx-2">{{ item.cantidadImprimir }}</span>
                          <v-btn icon x-small @click.stop="adjustQty(i, 1)">
                            <v-icon x-small>mdi-plus</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>

          </v-row>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-4">
          <v-btn text @click="showLabelDialog = false">Cancelar</v-btn>
          <v-spacer />
          <v-btn color="primary" :disabled="totalLabelCount === 0" @click="downloadMasterZPL">
            <v-icon left>mdi-download</v-icon>
            Descargar ZPL · {{ totalLabelCount }} etiqueta{{ totalLabelCount !== 1 ? 's' : '' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, getToday } from "@/services/webserver.js";
import createItem from "@/utils/createProduct.js";
import ProductForm from "@/components/ProductForm.vue";

export default {
  components: { ProductForm },
  data() {
    return {
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },
      sku_dialogo: false,
      suppliers: [],
      supplier: null,
      Product_dialog: false,
      errors: [],
      valid: false,
      item_dialogo: false,
      editDialog: false,
      edit_price: false,
      edit_discount: false,
      price_valid: false,
      discount_valid: false,
      editValid: false,
      newPrice: 0,
      loading_status: false,
      f_required: [(v) => !!v || "Requerido"],
      f_email: [
        (v) => !!v || "Correo valido",
        (v) => /.+@.+/.test(v) || "Correo no valido",
      ],
      imageUrl: null,
      dialog: false,
      header_sku: [
        {
          text: "EAN",
          align: "start",
          sortable: true,
          filterable: true,
          value: "ean",
          dataType: "text",
        },
        {
          text: "Categoría",
          align: "start",
          sortable: true,
          filterable: true,
          value: "categoria",
          dataType: "text",
        },
        {
          text: "Grupo",
          align: "start",
          sortable: true,
          filterable: true,
          value: "grupo",
          dataType: "text",
        },
        {
          text: "Marca",
          align: "start",
          sortable: true,
          filterable: true,
          value: "marca",
          dataType: "text",
        },
        {
          text: "Referencia",
          align: "start",
          sortable: true,
          filterable: true,
          value: "refe",
          dataType: "text",
        },
        {
          text: "Color",
          align: "start",
          sortable: true,
          filterable: true,
          value: "color",
          dataType: "text",
        },
        {
          text: "Talla",
          align: "start",
          sortable: true,
          filterable: true,
          value: "talla",
          dataType: "text",
        },
        {
          text: "Sistema",
          align: "start",
          sortable: true,
          filterable: true,
          value: "tallas",
          dataType: "text",
        },
        {
          text: "Costo",
          align: "end",
          sortable: true,
          filterable: true,
          value: "costo",
          dataType: "number",
        },
        {
          text: "Precio",
          align: "end",
          sortable: true,
          filterable: true,
          value: "precio",
          dataType: "number",
        },
        {
          text: "Descuento",
          align: "end",
          sortable: true,
          filterable: true,
          value: "descuento",
          dataType: "number",
        },
      ],
      minimalHeaders: [
        {
          text: "Producto",
          value: "product",
          sortable: false,
          width: "25%",
        },
        {
          text: "Categoría / Marca",
          value: "category",
          sortable: true,
          width: "15%",
        },
        {
          text: "Referencia / Color",
          value: "reference",
          sortable: true,
          width: "15%",
        },
        {
          text: "Tamaño",
          value: "size",
          align: "center",
          width: "10%",
        },
        {
          text: "Precios",
          value: "pricing",
          align: "end",
          width: "15%",
        },
        {
          text: "Stock Mínimo",
          value: "stock",
          align: "center",
          width: "10%",
        },
        {
          text: "Acciones",
          value: "actions",
          align: "center",
          sortable: false,
          width: "10%",
        },
      ],
      header_changes: [
        {
          text: "Categoría",
          align: "start",
          sortable: true,
          filterable: true,
          value: "categoria",
          dataType: "text",
        },
        {
          text: "Grupo",
          align: "start",
          sortable: true,
          filterable: true,
          value: "grupo",
          dataType: "text",
        },
        {
          text: "Referencia",
          align: "start",
          sortable: true,
          filterable: true,
          value: "refe",
          dataType: "text",
        },
        {
          text: "Color",
          align: "start",
          sortable: true,
          filterable: true,
          value: "color",
          dataType: "text",
        },
        {
          text: "Tamaño",
          align: "center",
          sortable: true,
          filterable: true,
          value: "tallas",
          dataType: "text",
        },
        {
          text: "Cantidad",
          align: "center",
          sortable: true,
          filterable: true,
          value: "quantity",
          dataType: "text",
        },
        {
          text: "Precio",
          align: "center",
          sortable: true,
          filterable: true,
          value: "price",
          dataType: "text",
        },
        {
          text: "Nuevo Precio",
          align: "center",
          sortable: true,
          filterable: true,
          value: "newPrice",
          dataType: "text",
        },
        {
          text: "Diferencia",
          align: "center",
          sortable: true,
          filterable: true,
          value: "diferencia",
          dataType: "text",
        },
      ],
      changes_lst: [],
      price_changes_lst: false,
      loading_changes: false,
      inventory_lst: [],
      inventory_filter: [],
      item: createItem(),
      selected: [],
      brands: [],
      categories: [],
      filters: { id_proveedor: null, refe: null, precio: null },
      search: "",
      itemEdit: createItem(),
      editItemData: createItem(),
      compressed_image: null,
      sku_lst: [],
      selected_skus: [],
      new_cost: null,
      new_price: null,
      // Labels WYSIWYG
      showLabelDialog: false,
      labelFormat: "cab",
      labelItems: [],
      previewIndex: 0,
      selectedFieldId: null,
      draggingFieldId: null,
      dragStart: { mx: 0, my: 0, fx: 0, fy: 0 },
      labelFields: [
        { id: "nombre",     label: "Nombre",      icon: "mdi-package-variant",  visible: true,  fontSize: 12, x: 1,  y: 1  },
        { id: "marca",      label: "Marca",        icon: "mdi-star-outline",     visible: false, fontSize: 8,  x: 20, y: 1  },
        { id: "referencia", label: "Referencia",  icon: "mdi-tag",              visible: true,  fontSize: 8,  x: 1,  y: 8  },
        { id: "talla",      label: "Talla",       icon: "mdi-ruler",            visible: true,  fontSize: 8,  x: 22, y: 8  },
        { id: "color",      label: "Color",       icon: "mdi-palette",          visible: false, fontSize: 8,  x: 1,  y: 13 },
        { id: "ean",        label: "EAN/Barcode", icon: "mdi-barcode",          visible: true,  fontSize: 7,  x: 1,  y: 13 },
        { id: "precio",     label: "Precio",      icon: "mdi-currency-usd",     visible: true,  fontSize: 16, x: 1,  y: 19 },
        { id: "costo",      label: "Costo (cod)", icon: "mdi-lock-outline",     visible: false, fontSize: 7,  x: 22, y: 19 },
        { id: "sku",        label: "SKU",         icon: "mdi-identifier",       visible: false, fontSize: 7,  x: 1,  y: 23 },
        { id: "fecha",      label: "Fecha/Lote",  icon: "mdi-calendar-outline", visible: false, fontSize: 7,  x: 20, y: 23 },
      ],
    };
  },
  computed: {
    totalLabelCount() {
      return this.labelItems.reduce((sum, i) => sum + (i.cantidadImprimir || 0), 0);
    },

    // px per mm for the canvas display
    labelScale() {
      return this.labelFormat === "cab" ? 7 : 6;
    },

    // canvas size in pixels
    labelCanvasW() {
      return this.labelFormat === "cab" ? Math.round(33 * this.labelScale) : Math.round(50 * this.labelScale);
    },

    labelCanvasH() {
      return this.labelFormat === "cab" ? Math.round(25 * this.labelScale) : Math.round(30 * this.labelScale);
    },
  },
  mounted() {
    this.get_supplier();
    this.loadLabelConfig();
  },
  methods: {
    canCreateProduct() {
      return this.filters.id_proveedor !== null;
    },
    handleProductRefresh() {
      // Si hay un proveedor seleccionado, recargar automáticamente la lista
      if (this.filters.id_proveedor) {
        this.get_inventory();
      } else {
        this.showMessage("Producto guardado exitosamente", "success");
      }
    },
    handleProductClose() {
      this.Product_dialog = false;
      // Resetear el item para el próximo uso
      this.item = createItem();

      // Si hay un proveedor seleccionado, recargar automáticamente la lista
      if (this.filters.id_proveedor) {
        this.get_inventory();
      }
    },
    // Método para mostrar mensajes en el snackbar
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    // Formatea números como moneda
    formatCurrency(value) {
      if (!value && value !== 0) return "$ 0";
      return "$ " + parseFloat(value).toLocaleString(2);
    },

    // Cuenta elementos únicos en un campo
    countUniqueItems(field) {
      if (!this.inventory_filter || this.inventory_filter.length === 0) return 0;
      const uniqueValues = new Set(this.inventory_filter.map((item) => item[field]));
      return uniqueValues.size;
    },

    // Calcula el valor del catálogo basado en precios
    calculateCatalogValue() {
      if (!this.inventory_filter || this.inventory_filter.length === 0) return 0;
      return this.inventory_filter.reduce((total, item) => {
        const precio = parseFloat(item.precio || 0);
        return total + precio;
      }, 0);
    },

    // Calcula el total de stock mínimo
    calculateTotalMinStock() {
      if (!this.inventory_filter || this.inventory_filter.length === 0) return 0;
      return this.inventory_filter.reduce((total, item) => {
        return total + parseFloat(item.cantidad || 0);
      }, 0);
    },

    // Cuenta tallas únicas
    countUniqueSizes() {
      if (!this.inventory_filter || this.inventory_filter.length === 0) return 0;
      const uniqueSizes = new Set(this.inventory_filter.map((item) => item.talla));
      return uniqueSizes.size;
    },

    // Get stock color based on quantity
    getStockColor(cantidad) {
      const qty = parseFloat(cantidad || 0);
      if (qty === 0) return "error";
      if (qty < 5) return "warning";
      return "success";
    },

    // Método para exportar a CSV
    exportToCSV() {
      if (!this.inventory_filter || this.inventory_filter.length === 0) {
        this.showMessage("No hay datos para exportar", "warning");
        return;
      }
      this.exportJSONToCSV(this.inventory_filter);
    },

    exportJSONToCSV(data) {
      const headers = [
        "PLU",
        "SKU",
        "EAN",
        "Producto",
        "Categoría",
        "Grupo",
        "Marca",
        "Referencia",
        "Color",
        "Tamaño",
        "Sistema",
        "Costo",
        "Precio",
        "Descuento",
        "Stock Mínimo",
      ];

      const rows = data.map((item) => [
        item.plu || "",
        item.sku || "",
        item.ean || "",
        item.nombre || "",
        item.categoria || "",
        item.grupo || "",
        item.marca || "",
        item.refe || "",
        item.color || "",
        item.talla || "",
        item.tallas || "",
        item.costo || 0,
        item.precio || 0,
        item.descuento || 0,
        item.cantidad || 0,
      ]);

      const csv = [headers, ...rows]
        .map((row) =>
          row.map((cell) => `"${cell.toString().replace(/"/g, '""')}"`).join(",")
        )
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `inventario_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      this.showMessage("Archivo exportado exitosamente", "success");
    },

    update_selected_values() {
      if (!this.selected_skus.length) return;

      this.selected_skus.forEach((item) => {
        if (this.new_cost) item.costo = parseFloat(this.new_cost);
        if (this.new_price) item.precio = parseFloat(this.new_price);
      });
    },

    update_sku_values() {
      if (this.sku_lst.length === 0) return;
      const firstItem = this.sku_lst[0];

      this.sku_lst.forEach((element) => {
        element.costo = parseFloat(firstItem.costo);
        element.precio = parseFloat(firstItem.precio);
      });

      // También actualizar los elementos seleccionados
      this.selected_skus = [...this.sku_lst];

      this.showMessage("Valores del primer elemento aplicados a todos", "success");
    },

    newForm() {
      this.item = createItem();

      // Si hay un proveedor seleccionado, pre-cargarlo en el nuevo producto
      if (this.filters.id_proveedor) {
        this.item.id_proveedor =
          this.filters.id_proveedor.code || this.filters.id_proveedor.id_nit;
      }

      this.Product_dialog = true;
    },

    get_supplier() {
      this.loading_status = true;
      const qry = {
        store: window.profile.store_id,
        table: "proveedores",
        filters: [
          {
            field: "store",
            operator: "=",
            value: window.profile.store_id.substring(0, 6),
          },
        ],
      };
      console.log("get_supplier", qry, window.profile);
      webserver(
        "get_dataTable",
        qry,
        (data) => {
          this.loading_status = false;
          console.log(data);
          this.suppliers = data.map((itm) => ({
            ...itm,
            code: itm.codigo,
            PartyIdentification: itm.id_nit,
            PartyName: itm.nombre,
            AddressLine: itm.direccion,
            CityName: itm.Ciudad,
            client_email: itm.email,
            client_phone: itm.telefono,
          }));
          this.showMessage(`${this.suppliers.length} proveedores cargados`, "success");
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al cargar proveedores: " + error, "error");
        }
      );
    },

    get_inventory() {
      if (!this.filters.id_proveedor) {
        this.showMessage("Debe seleccionar un proveedor", "warning");
        return;
      }

      // Limpiar lista actual
      this.inventory_lst = [];
      this.inventory_filter = [];

      const qry = {
        store: window.profile.store_id,
        table: "items",
        groupby: "sku",
        filters: [],
      };

      qry.filters.push({
        field: "id_pos",
        operator: "=",
        value: window.profile.store_id.substring(0, 6),
      });

      if (this.filters.id_proveedor.code || this.filters.id_proveedor.id_nit) {
        qry.filters.push({
          field: "id_proveedor",
          operator: "=",
          value: this.filters.id_proveedor.code || this.filters.id_proveedor.id_nit,
        });
      } else {
        this.showMessage("Proveedor seleccionado no válido", "warning");
        return;
      }

      if (this.filters.refe) {
        qry.filters.push({
          field: "refe",
          operator: " LIKE ",
          value: `%${this.filters.refe}%`,
        });
      }

      this.loading_status = true;
      this.showMessage("Actualizando lista de productos...", "info");
      console.log(qry);

      webserver(
        "get_dataTable",
        qry,
        (data) => {
          this.loading_status = false;

          if (data && data.length > 0) {
            this.inventory_lst = data;
            this.inventory_filter = data;
            this.showMessage(`${data.length} productos actualizados`, "success");
          } else {
            this.inventory_lst = [];
            this.inventory_filter = [];
            this.showMessage("No se encontraron productos para este proveedor", "info");
          }
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al cargar productos: " + error, "error");
        }
      );
    },

    get_curva(e) {
      if (!e.sku) {
        this.showMessage("No se puede obtener el detalle sin un SKU válido", "warning");
        return;
      }
      const qry = {
        store: window.profile.store_id,
        table: "items",
        filters: [],
      };
      qry.filters.push({
        field: "id_pos",
        operator: "=",
        value: window.profile.store_id,
      });
      if (e.sku) {
        qry.filters.push({
          field: "sku",
          operator: "=",
          value: e.sku,
        });
      } else return;

      this.loading_status = true;
      this.showMessage("Cargando detalles del producto...", "info");

      webserver(
        "get_dataTable",
        qry,
        (data) => {
          this.loading_status = false;

          if (data && data.length > 0) {
            this.sku_lst = data;
            this.selected_skus = []; // Limpiar selecciones previas
            this.new_cost = null;
            this.new_price = null;
            this.sku_dialogo = true;
            this.showMessage(`${data.length} SKUs cargados`, "success");
          } else {
            this.sku_lst = [];
            this.showMessage("No se encontraron SKUs para este producto", "warning");
          }
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al cargar SKUs: " + error, "error");
        }
      );
    },

    get_changes() {
      this.loading_changes = true;
      const qry = { store: window.store.store_id, issueDate: getToday() };
      webserver("get_inventory_pc", qry, (data) => {
        this.loading_changes = false;
        this.changes_lst = data.map((itm) => ({
          ...itm,
          diferencia: parseFloat(itm.diferencia),
          price: parseFloat(itm.price),
          quantity: parseFloat(itm.quantity),
          newPrice: parseFloat(itm.newPrice),
        }));
      });
    },

    GetSortOrder(prop) {
      return (a, b) => (a[prop] > b[prop] ? 1 : a[prop] < b[prop] ? -1 : 0);
    },

    getImageURL(marca, refe) {
      const account = window.store.store_id.substring(0, 6);
      return (
        this.compressed_image ||
        `https://moda-stc.com/photos/${account}/${marca}/${refe}.jpg`
      );
    },

    chooseImage() {
      this.$refs.fileInput.click();
    },

    onSelectFile(dat) {
      const input = this.$refs.fileInput;
      const files = input.files;
      if (files && files[0]) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const prct = 800 / img.width;
            const elem = document.createElement("canvas");
            elem.width = img.width * prct;
            elem.height = img.height * prct;
            const ctx = elem.getContext("2d");
            ctx.drawImage(img, 0, 0, img.width * prct, img.height * prct);
            const imageUrl = elem.toDataURL("image/jpeg");
            this.compressed_image = imageUrl;
            dat.picture = files[0].name;
            this.upload_photo(dat.marca, `${dat.refe.toLowerCase()}.jpg`, imageUrl);
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(files[0]);
        this.item.picture = files[0].name.toLowerCase();
      }
    },

    upload_photo(marca, picture, data) {
      const qry = { store: window.store.store_id, photo: data, marca, picture };
      webserver("upload_photo", qry, () => {
        this.compressed_image = null;
      });
    },

    // ── ETIQUETAS WYSIWYG ─────────────────────────────────────────────────

    loadLabelConfig() {
      try {
        const saved = localStorage.getItem("master_label_config");
        if (!saved) return;
        const data = JSON.parse(saved);
        if (data.labelFormat) this.labelFormat = data.labelFormat;
        if (Array.isArray(data.labelFields)) {
          data.labelFields.forEach((sf) => {
            const field = this.labelFields.find((f) => f.id === sf.id);
            if (!field) return;
            field.visible = sf.visible;
            field.fontSize = sf.fontSize;
            field.x = sf.x;
            field.y = sf.y;
          });
        }
      } catch (_) { /* ignorar localStorage corrupto */ }
    },

    saveLabelConfig() {
      const data = {
        labelFormat: this.labelFormat,
        labelFields: this.labelFields.map((f) => ({
          id: f.id, visible: f.visible, fontSize: f.fontSize, x: f.x, y: f.y,
        })),
      };
      localStorage.setItem("master_label_config", JSON.stringify(data));
    },

    // Devuelve el item que se muestra en el canvas
    getPreviewItem() {
      return this.labelItems[this.previewIndex] || this.labelItems[0] || {
        nombre: "Camiseta Ejemplo", refe: "CAM001", talla: "M", color: "Azul",
        ean: "7706123456789", precio: 59900, descuento: 0, marca: "Marca", sku: "CAM001-M-AZU",
      };
    },

    // Retorna el estilo CSS para un campo en el canvas
    getFieldCanvasStyle(field) {
      return {
        position: "absolute",
        left: Math.round(field.x * this.labelScale) + "px",
        top: Math.round(field.y * this.labelScale) + "px",
        cursor: this.draggingFieldId === field.id ? "grabbing" : "grab",
        padding: "1px 2px",
        borderRadius: "2px",
        zIndex: this.selectedFieldId === field.id ? 10 : 1,
        userSelect: "none",
      };
    },

    // Convierte fontSize (pt) a píxeles de canvas
    getFieldCanvasFontSize(field) {
      // 1pt = 0.353mm; at labelScale px/mm → px = pt * 0.353 * scale
      return Math.max(6, Math.round(field.fontSize * 0.353 * this.labelScale));
    },

    // Retorna el texto a mostrar en el canvas para un campo
    getFieldDisplayValue(field) {
      const item = this.getPreviewItem();
      const price = parseFloat(item.precio || 0) * (1 - parseFloat(item.descuento || 0) / 100);
      const today = new Date();
      const dateStr = `${today.getDate().toString().padStart(2, "0")}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getFullYear().toString().slice(-2)}`;
      switch (field.id) {
        case "nombre":     return (item.nombre || item.refe || "Nombre").substring(0, 22);
        case "referencia": return `Ref: ${item.refe || "REF001"}`;
        case "talla":      return `T: ${item.talla || "M"}`;
        case "color":      return item.color || "Color";
        case "precio":     return `$ ${price.toLocaleString("es-CO", { minimumFractionDigits: 0 })}`;
        case "marca":      return item.marca || "Marca";
        case "costo":      return `AD${Math.round(parseFloat(item.costo || 0))}XC`;
        case "sku":        return `SKU: ${item.sku || "SKU-001"}`;
        case "fecha":      return dateStr;
        default:           return field.label;
      }
    },

    // Muestra/oculta un campo
    toggleFieldVisibility(field) {
      this.$set(field, "visible", !field.visible);
    },

    // Cambia el tamaño de fuente de un campo (reactivo)
    changeFontSize(field, delta) {
      const newSize = Math.max(5, Math.min(60, (field.fontSize || 10) + delta));
      this.$set(field, "fontSize", newSize);
    },

    // Inicia el arrastre de un campo en el canvas
    startFieldDrag(event, field) {
      this.draggingFieldId = field.id;
      this.selectedFieldId = field.id;
      this.dragStart = {
        mx: event.clientX,
        my: event.clientY,
        fx: field.x,
        fy: field.y,
      };
    },

    // Actualiza posición mientras arrastra
    onCanvasMouseMove(event) {
      if (!this.draggingFieldId) return;
      const dx = (event.clientX - this.dragStart.mx) / this.labelScale;
      const dy = (event.clientY - this.dragStart.my) / this.labelScale;
      const field = this.labelFields.find((f) => f.id === this.draggingFieldId);
      if (!field) return;
      const maxW = this.labelFormat === "cab" ? 33 : 50;
      const maxH = this.labelFormat === "cab" ? 25 : 30;
      this.$set(field, "x", Math.max(0, Math.min(maxW - 2, parseFloat((this.dragStart.fx + dx).toFixed(1)))));
      this.$set(field, "y", Math.max(0, Math.min(maxH - 2, parseFloat((this.dragStart.fy + dy).toFixed(1)))));
    },

    // Finaliza el arrastre
    onCanvasMouseUp() {
      this.draggingFieldId = null;
    },

    // Restablece las posiciones por defecto
    resetLabelPositions() {
      const defaults = {
        nombre:     { x: 1,  y: 1  }, marca:  { x: 20, y: 1  },
        referencia: { x: 1,  y: 8  }, talla:  { x: 22, y: 8  },
        color:      { x: 1,  y: 13 }, ean:    { x: 1,  y: 13 },
        precio:     { x: 1,  y: 19 }, costo:  { x: 22, y: 19 },
        sku:        { x: 1,  y: 23 }, fecha:  { x: 20, y: 23 },
      };
      this.labelFields.forEach((f) => {
        if (defaults[f.id]) {
          this.$set(f, "x", defaults[f.id].x);
          this.$set(f, "y", defaults[f.id].y);
        }
      });
    },

    openLabelDialog() {
      if (!this.selected_skus.length) {
        this.showMessage("Seleccione productos para imprimir etiquetas", "warning");
        return;
      }
      this.labelItems = this.selected_skus.map((s) => ({ ...s, cantidadImprimir: 1 }));
      this.previewIndex = 0;
      this.selectedFieldId = null;
      this.showLabelDialog = true;
    },

    adjustQty(i, delta) {
      const newVal = (this.labelItems[i].cantidadImprimir || 0) + delta;
      this.$set(this.labelItems[i], "cantidadImprimir", Math.max(0, newVal));
    },

    downloadMasterZPL() {
      let zpl = "";
      if (this.labelFormat === "cab") {
        zpl = this._generateCABZpl();
      } else {
        this.labelItems.forEach((item) => {
          for (let i = 0; i < (item.cantidadImprimir || 0); i++) {
            zpl += this._generateZebraLabel(item);
          }
        });
      }
      const blob = new Blob([zpl], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `etiquetas_${new Date().toISOString().slice(0, 10)}.zpl`;
      a.click();
      window.URL.revokeObjectURL(url);
      this.showLabelDialog = false;
      this.showMessage(`ZPL generado: ${this.totalLabelCount} etiquetas`, "success");
    },

    // CAB: 3 etiquetas por fila. Cada etiqueta es 33×25mm = 264×200 dots @203dpi
    _generateCABZpl() {
      const allLabels = [];
      this.labelItems.forEach((item) => {
        for (let i = 0; i < (item.cantidadImprimir || 0); i++) allLabels.push(item);
      });
      // xOffsets en dots: etiqueta 1 empieza en ~45, 2 en ~309, 3 en ~573 (ajustar según impresora)
      const colDots = Math.round(33 * 8); // 264 dots por columna
      const xOffsets = [5, 5 + colDots, 5 + colDots * 2];
      let zpl = "";
      for (let i = 0; i < allLabels.length; i += 3) {
        zpl += "^XA\n";
        for (let j = 0; j < 3 && i + j < allLabels.length; j++) {
          zpl += this._buildCABColumn(allLabels[i + j], xOffsets[j]);
        }
        zpl += "^XZ\n";
      }
      return zpl;
    },

    // Genera los comandos ZPL para una columna de la etiqueta CAB
    _buildCABColumn(item, xOffset) {
      const price = parseFloat(item.precio || 0) * (1 - parseFloat(item.descuento || 0) / 100);
      const today = new Date();
      const dateStr = `${today.getDate().toString().padStart(2, "0")}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getFullYear().toString().slice(-2)}`;
      let zpl = "";

      for (const field of this.labelFields) {
        if (!field.visible) continue;
        // Posición en dots: mm * 8 + offset de columna para X; mm * 8 para Y
        const xd = Math.round(field.x * 8) + xOffset;
        const yd = Math.round(field.y * 8);
        const fh = Math.max(8, Math.round(field.fontSize * 2.82)); // pt → dots

        switch (field.id) {
          case "nombre":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${(item.nombre || item.refe || "").substring(0, 18)}^FS\n`;
            break;
          case "referencia":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDRef:${(item.refe || "").substring(0, 10)}^FS\n`;
            break;
          case "talla":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDT:${item.talla || ""}^FS\n`;
            break;
          case "color":
            if (item.color) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${item.color}^FS\n`;
            break;
          case "marca":
            if (item.marca) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${item.marca}^FS\n`;
            break;
          case "sku":
            if (item.sku) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDSKU:${item.sku}^FS\n`;
            break;
          case "precio":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${price.toLocaleString("es-CO", { minimumFractionDigits: 0 })}^FS\n`;
            break;
          case "costo":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDAD${Math.round(parseFloat(item.costo || 0))}XC^FS\n`;
            break;
          case "ean":
            if (item.ean) {
              const bh = Math.round(3.5 * 8); // 3.5mm en dots
              zpl += `^FO${xd},${yd}^BY1,2,${bh}^BCN,${bh},N,N,N^FD${item.ean}^FS\n`;
              zpl += `^FO${xd},${yd + bh + 2}^A0N,10,10^FD${item.ean}^FS\n`;
            }
            break;
          case "fecha":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${dateStr}^FS\n`;
            break;
        }
      }
      return zpl;
    },

    // Genera etiqueta Zebra 50×30mm (400×240 dots @203dpi)
    _generateZebraLabel(item) {
      const price = parseFloat(item.precio || 0) * (1 - parseFloat(item.descuento || 0) / 100);
      const today = new Date();
      const dateStr = `${today.getDate().toString().padStart(2, "0")}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getFullYear().toString().slice(-2)}`;
      let zpl = "^XA\n^PW400\n^LL240\n";

      for (const field of this.labelFields) {
        if (!field.visible) continue;
        const xd = Math.round(field.x * 8);
        const yd = Math.round(field.y * 8);
        const fh = Math.max(8, Math.round(field.fontSize * 2.82));

        switch (field.id) {
          case "nombre":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${(item.nombre || item.refe || "").substring(0, 26)}^FS\n`;
            break;
          case "referencia":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDRef: ${item.refe || ""}^FS\n`;
            break;
          case "talla":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDT: ${item.talla || ""}^FS\n`;
            break;
          case "color":
            if (item.color) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${item.color}^FS\n`;
            break;
          case "marca":
            if (item.marca) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${item.marca}^FS\n`;
            break;
          case "sku":
            if (item.sku) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDSKU: ${item.sku}^FS\n`;
            break;
          case "precio":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD$ ${price.toLocaleString("es-CO", { minimumFractionDigits: 0 })}^FS\n`;
            break;
          case "costo":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDAD${Math.round(parseFloat(item.costo || 0))}XC^FS\n`;
            break;
          case "ean":
            if (item.ean) {
              const bh = Math.round(5 * 8); // 5mm en dots
              zpl += `^FO${xd},${yd}^BY2,2,${bh}^BCN,${bh},Y,N,N^FD${item.ean}^FS\n`;
            }
            break;
          case "fecha":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${dateStr}^FS\n`;
            break;
        }
      }
      zpl += "^XZ\n";
      return zpl;
    },
    // ─────────────────────────────────────────────────────────────────────

    filterData() {
      const { filters } = this;
      this.inventory_filter = this.inventory_lst.filter((item) =>
        Object.keys(filters).every(
          (key) =>
            filters[key] === null || filters[key] === "" || item[key] === filters[key]
        )
      );
    },

    editItem(data) {
      this.item = { ...data }; // Crear copia para evitar mutaciones
      this.Product_dialog = true;
    },

    openEditDialog(item) {
      this.editItemData = { ...item };
      this.editDialog = true;
    },

    closeEditDialog() {
      this.editDialog = false;
    },

    saveEdit() {
      if (this.$refs.editForm.validate()) {
        const index = this.inventory_lst.findIndex(
          (i) => i.ean === this.editItemData.ean
        );
        if (index !== -1) {
          this.inventory_lst.splice(index, 1, this.editItemData);
          this.inventory_filter = [...this.inventory_lst];
          this.editDialog = false;
        }
      }
    },

    discount_change(e) {
      e.preventDefault();
      if (this.$refs.discountChangeform.validate() && this.discount_valid) {
        const qry = {
          store: window.store.store_id,
          newprice: parseInt(this.newPrice),
          products: this.selected,
          userId: window.userId,
        };
        if (qry.newprice > 0) {
          webserver("changeDiscount", qry, () => {
            this.get_inventory();
            this.edit_discount = false;
          });
        }
      }
    },

    price_change(e) {
      e.preventDefault();
      this.price_valid = this.$refs.priceChangeform.validate();

      if (this.price_valid && this.selected_skus.length > 0) {
        this.loading_status = true;
        this.showMessage("Aplicando cambios de precio...", "info");
        const qry = {
          store: window.profile.store_id,
          table: "items",
          data: this.selected_skus, // Solo enviar los items seleccionados
        };

        webserver(
          "put_table",
          qry,
          () => {
            this.loading_status = false;
            this.sku_dialogo = false;
            this.selected_skus = []; // Limpiar selección
            this.new_cost = null;
            this.new_price = null;

            // Actualizar la lista principal
            this.get_inventory();
            this.showMessage("Cambios de precio aplicados con éxito", "success");
          },
          (error) => {
            this.loading_status = false;
            this.showMessage("Error al aplicar cambios de precio: " + error, "error");
          }
        );
      } else {
        if (this.selected_skus.length === 0) {
          this.showMessage("Debe seleccionar al menos un SKU", "warning");
        } else {
          this.showMessage("Por favor complete los campos requeridos", "warning");
        }
      }
    },
  },
  watch: {
    price_changes_lst(visible) {
      if (visible) {
        this.get_changes();
      }
    },
    "filters.id_proveedor"(newValue, oldValue) {
      // Solo recargar automáticamente si cambia el proveedor y ya había uno seleccionado
      if (newValue && oldValue && newValue.code !== oldValue.code) {
        this.get_inventory();
      }
    },
    labelFields: {
      handler() { this.saveLabelConfig(); },
      deep: true,
    },
    labelFormat() { this.saveLabelConfig(); },
  },
};
</script>

<style scoped>
/* KPI Cards */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 28px !important;
}

.kpi-content {
  flex: 1;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

/* Main Card */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.dialog-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
}

/* Data Table */
.minimal-table {
  border: none !important;
}

.minimal-table >>> .v-data-table__wrapper {
  border: none;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
  padding: 16px !important;
}

.minimal-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
  transition: background-color 0.2s;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.minimal-table >>> tbody tr td {
  padding: 16px !important;
  font-size: 0.875rem !important;
}

/* Product Info */
.product-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.product-name {
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
}

.product-details {
  font-size: 0.75rem;
  color: #666;
}

/* Category Info */
.category-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Reference Info */
.reference-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Size Info */
.size-info {
  text-align: center;
}

/* Pricing Info */
.pricing-info {
  text-align: right;
}

.price-main {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.price-cost {
  color: #666;
}

.price-discount {
  font-weight: 500;
}

/* Stock Info */
.stock-info {
  text-align: center;
}

/* Loading */
.minimal-table >>> .v-data-table__progress {
  height: 2px !important;
}

/* ── Label Editor WYSIWYG ─────────────────────── */
.editor-section-label {
  font-size: 0.68rem;
  font-weight: 700;
  color: #999;
  letter-spacing: 0.08em;
}
.format-toggle {
  width: 100%;
}
.format-btn {
  flex: 1 !important;
  font-size: 0.75rem !important;
}

/* Config panel */
.wysiwyg-config-panel {
  background: #fafafa;
  border-right: none;
  min-width: 210px;
}
.wysiwyg-field-row {
  display: flex;
  align-items: center;
  padding: 4px 2px;
  border-radius: 5px;
  cursor: pointer;
  margin-bottom: 1px;
  transition: background 0.12s;
  user-select: none;
}
.wysiwyg-field-row:hover {
  background: #f0f4ff;
}
.wysiwyg-field-selected {
  background: #e3edff;
}
.label-field-name {
  font-size: 0.72rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90px;
}
.font-size-badge {
  font-size: 0.7rem;
  font-weight: 700;
  color: #555;
  min-width: 18px;
  text-align: center;
}

/* WYSIWYG canvas */
.canvas-area {
  display: flex;
  align-items: flex-start;
}
.label-canvas {
  position: relative;
  background: #ffffff;
  border: 2px dashed #bbb;
  border-radius: 3px;
  box-shadow: 2px 3px 12px rgba(0,0,0,0.12);
  overflow: hidden;
  cursor: default;
  flex-shrink: 0;
}
.canvas-field {
  position: absolute;
  display: inline-block;
  line-height: 1.1;
}
.canvas-field-selected {
  outline: 1px dashed #1976d2;
  background: rgba(25, 118, 210, 0.05);
  border-radius: 2px;
}
.canvas-field-dragging {
  opacity: 0.75;
}
.canvas-barcode {
  background: repeating-linear-gradient(
    90deg,
    #000 0, #000 1.5px,
    #fff 1.5px, #fff 3px
  );
  width: 100%;
}

/* Quantity table */
.label-items-table th {
  font-size: 0.72rem !important;
  color: #666 !important;
  font-weight: 600 !important;
  padding: 6px 10px !important;
  background: #fafafa !important;
}
.label-items-table td {
  padding: 6px 10px !important;
  border-bottom: 1px solid #f0f0f0;
}
.qty-row {
  cursor: pointer;
  transition: background 0.15s;
}
.qty-row:hover {
  background: #f5f5f5;
}
.active-preview-row {
  background: #e8f0fe !important;
}
.qty-value {
  font-size: 0.95rem;
  font-weight: 700;
  min-width: 22px;
  text-align: center;
  color: #333;
}

/* Responsive */
@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
  }

  .minimal-toolbar {
    flex-wrap: wrap;
    gap: 16px;
  }
}
</style>
